import { ref, computed } from 'vue'
import { Device } from '@capacitor/device'
import { config } from '../config'
import { QrSessionApi } from '../api/QrSessionApi'
import { useUserManagerStore } from '../stores/userManagerStore'
import log from '../utils/logger'

export type QrConfirmPhase = 'idle' | 'sending' | 'success' | 'error'

const isOpen = ref(false)
const nonce = ref('')
const authority = ref('')
const phase = ref<QrConfirmPhase>('idle')
const errorKey = ref<string | null>(null)

// Stores a rejection i18n key for the dialog component to translate and show via snackbar
const pendingRejection = ref<string | null>(null)

const verificationCode = computed(() => nonce.value.slice(-4).toUpperCase())

export function openQrLoginConfirm(nonceParam: string, authorityParam: string) {
  log.debug(`[GO-DEBUG][QR-CONFIRM] openQrLoginConfirm called — nonce=${nonceParam.slice(-8)}, authority=${authorityParam}`)

  const userManagerStore = useUserManagerStore()

  log.debug(`[GO-DEBUG][QR-CONFIRM] config.authority=${config.authority}`)
  log.debug(`[GO-DEBUG][QR-CONFIRM] userManagerStore.token=${!!userManagerStore.token}`)

  if (authorityParam && authorityParam !== config.authority) {
    if (import.meta.env.PROD) {
      pendingRejection.value = 'qrlogin_confirm.error_authority_mismatch'
      log.warn(`[GO-DEBUG][QR-CONFIRM] Authority mismatch: received="${authorityParam}" expected="${config.authority}"`)
      return
    }
    log.warn(`[GO-DEBUG][QR-CONFIRM] Authority mismatch (ignored in dev): received="${authorityParam}" expected="${config.authority}"`)
  }

  if (!userManagerStore.token) {
    pendingRejection.value = 'qrlogin_confirm.error_not_authenticated'
    log.warn('[GO-DEBUG][QR-CONFIRM] User not authenticated — no token in store')
    return
  }

  log.debug(`[GO-DEBUG][QR-CONFIRM] Validation passed — opening dialog`)
  nonce.value = nonceParam
  authority.value = authorityParam
  phase.value = 'idle'
  errorKey.value = null
  isOpen.value = true
  log.debug(`[GO-DEBUG][QR-CONFIRM] isOpen=${isOpen.value}, phase=${phase.value}`)
}

async function approveQrLogin() {
  log.debug(`[GO-DEBUG][QR-CONFIRM] approveQrLogin called — nonce=${nonce.value.slice(-8)}`)
  phase.value = 'sending'
  errorKey.value = null

  try {
    const deviceId = await Device.getId()
    log.debug(`[GO-DEBUG][QR-CONFIRM] deviceId=${deviceId.identifier}`)

    const status = await QrSessionApi.transfer(nonce.value, deviceId.identifier)
    log.debug(`[GO-DEBUG][QR-CONFIRM] transfer response status=${status}`)

    switch (status) {
      case 204:
        log.debug(`[GO-DEBUG][QR-CONFIRM] → success`)
        phase.value = 'success'
        break
      case 401:
        log.warn(`[GO-DEBUG][QR-CONFIRM] → 401 expired token`)
        phase.value = 'error'
        errorKey.value = 'qrlogin_confirm.error_expired_token'
        break
      case 409:
        log.warn(`[GO-DEBUG][QR-CONFIRM] → 409 nonce already used`)
        phase.value = 'error'
        errorKey.value = 'qrlogin_confirm.error_nonce_used'
        break
      case 422:
        log.warn(`[GO-DEBUG][QR-CONFIRM] → 422 invalid body`)
        phase.value = 'error'
        errorKey.value = 'qrlogin_confirm.error_invalid'
        break
      default:
        log.warn(`[GO-DEBUG][QR-CONFIRM] → unexpected status ${status}`)
        phase.value = 'error'
        errorKey.value = 'qrlogin_confirm.error_network'
    }
  } catch (e) {
    const err = e as any
    log.error(`[GO-DEBUG][QR-CONFIRM] Transfer exception: message=${err?.message}, status=${err?.response?.status}, url=${err?.config?.url}, data=${JSON.stringify(err?.response?.data)}`)
    phase.value = 'error'
    errorKey.value = 'qrlogin_confirm.error_network'
  }
}

function closeQrLoginConfirm() {
  isOpen.value = false
  nonce.value = ''
  authority.value = ''
  phase.value = 'idle'
  errorKey.value = null
}

export function useQrLoginConfirm() {
  return {
    isOpen,
    nonce,
    authority,
    phase,
    errorKey,
    pendingRejection,
    verificationCode,
    openQrLoginConfirm,
    approveQrLogin,
    closeQrLoginConfirm,
  }
}
